import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { QRCodeCanvas } from 'qrcode.react';
import { QrCode, X } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_link-builder-22/artifacts/o4eavll4_a942b2bb-6513-4b93-b220-bd15a0be07b2.jpg';

const VIP_STYLES = {
  diavelico: { name: 'Diavélico', bg: 'from-neutral-900 to-black', border: 'border-diavel-red/30', badge: 'bg-diavel-red/20 text-diavel-red' },
  silver: { name: 'Diavélico Silver', bg: 'from-zinc-800 to-neutral-900', border: 'border-zinc-400/30', badge: 'bg-zinc-400/20 text-zinc-300' },
  gold: { name: 'Diavélico Gold', bg: 'from-yellow-900/40 to-neutral-900', border: 'border-yellow-500/30', badge: 'bg-yellow-500/20 text-yellow-400' },
  diamond: { name: 'Diavélico Diamond', bg: 'from-cyan-900/30 to-neutral-900', border: 'border-cyan-400/30', badge: 'bg-cyan-400/20 text-cyan-300' },
};

export default function DigitalCard() {
  const { user } = useAuth();
  const vip = VIP_STYLES[user?.vip_level] || VIP_STYLES.diavelico;
  const [showQR, setShowQR] = useState(false);

  // QR contains user info for studio scanning
  const qrData = JSON.stringify({
    id: user?.user_id,
    name: user?.name,
    code: user?.referral_code,
    vip: user?.vip_level,
    balance: user?.balance,
  });

  return (
    <>
      <div className="relative w-full max-w-md mx-auto" data-testid="digital-card">
        <div className={`relative aspect-[1.586] rounded-xl overflow-hidden shadow-2xl border ${vip.border} glow-pulse`}>
          {/* Card background */}
          <div className={`absolute inset-0 bg-gradient-to-br ${vip.bg}`} />
          
          {/* Noise texture */}
          <div className="absolute inset-0 opacity-10 mix-blend-overlay" 
            style={{ backgroundImage: "url('https://grainy-gradients.vercel.app/noise.svg')" }} />
          
          {/* Shine effect */}
          <div className="absolute inset-0 card-shine" />
          
          {/* Card content */}
          <div className="relative h-full p-5 sm:p-6 flex flex-col justify-between">
            {/* Top section */}
            <div className="flex justify-between items-start">
              <div>
                <img src={LOGO_URL} alt="Diavel" className="w-10 h-10 sm:w-12 sm:h-12 object-contain opacity-90" />
              </div>
              <div className={`px-3 py-1 ${vip.badge} font-mono text-[10px] tracking-widest uppercase`}>
                {vip.name}
              </div>
            </div>

            {/* Balance */}
            <div className="flex items-end justify-between">
              <div>
                <p className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-1">Saldo disponible</p>
                <p className="font-heading font-extrabold text-3xl sm:text-4xl text-white tracking-tight" data-testid="card-balance">
                  {(user?.balance || 0).toFixed(2)}€
                </p>
              </div>
              {/* QR Button on card */}
              <button
                onClick={() => setShowQR(true)}
                data-testid="show-qr-btn"
                className="w-12 h-12 sm:w-14 sm:h-14 bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-white/20 transition-all duration-300 rounded-lg group"
              >
                <QrCode className="w-6 h-6 sm:w-7 sm:h-7 text-white/80 group-hover:text-white transition-colors" />
              </button>
            </div>

            {/* Bottom section */}
            <div className="flex justify-between items-end">
              <div>
                <p className="font-mono text-[10px] tracking-widest uppercase text-zinc-600 mb-1">Miembro</p>
                <p className="text-sm font-semibold text-white tracking-wide">{user?.name}</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-[10px] tracking-widest uppercase text-zinc-600 mb-1">INFERNO</p>
                <p className="font-heading font-bold text-xs tracking-wider uppercase text-zinc-400">Diavel Tattoo</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQR && (
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-6"
          data-testid="qr-modal"
          onClick={() => setShowQR(false)}
        >
          <div 
            className="bg-diavel-card border border-white/10 p-8 max-w-sm w-full text-center animate-fade-in-up"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="font-heading font-bold text-lg uppercase tracking-wider text-white text-left">Tu código QR</h3>
                <p className="text-xs text-zinc-500 text-left mt-1">Muéstralo en el estudio para identificarte</p>
              </div>
              <button 
                onClick={() => setShowQR(false)} 
                data-testid="close-qr-btn"
                className="text-zinc-500 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* QR Code */}
            <div className="bg-white p-6 rounded-lg mx-auto inline-block mb-6">
              <QRCodeCanvas
                value={qrData}
                size={220}
                bgColor="#FFFFFF"
                fgColor="#050505"
                level="H"
                includeMargin={false}
              />
            </div>

            {/* User info below QR */}
            <div className="space-y-2">
              <p className="font-heading font-bold text-white text-lg">{user?.name}</p>
              <div className={`inline-flex items-center gap-2 px-3 py-1 ${vip.badge}`}>
                <span className="font-mono text-[10px] tracking-widest uppercase">{vip.name}</span>
              </div>
              <p className="font-mono text-sm text-zinc-400 tracking-wider mt-2">
                Código: <span className="text-white font-bold">{user?.referral_code}</span>
              </p>
              <p className="font-heading font-bold text-2xl text-white mt-3">
                {(user?.balance || 0).toFixed(2)}€
              </p>
              <p className="font-mono text-[10px] tracking-widest uppercase text-zinc-600">Saldo disponible</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
